// -----------------------------------------------------------------------
// <copyright file="AnnouncementFloat.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { IBaseProps } from '@fluentui/react';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Notifications } from '../../../common/notifications/Notifications';
import { NotificationModel } from '../../../common/notifications/Notifications.model';
import { IAppState } from '../../../web-legacy/models/reduxState';
import {
  getHelpConfigSelector,
  getThemeSelector,
  isGetHelpSelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import {
  dispatchDismissUserNotice,
  dispatchOnNotificationsRender,
} from '../../../web-legacy/packages/contactUs/reduxUpdater/dispatchers/contactUsDispatchers';
import { notificationsSelector } from '../../../web-legacy/packages/contactUs/reduxUpdater/selectors/sharedStateSelectors';
import { IContactUsCurrentTheme } from '../../../web-legacy/packages/contactUs/styles/theme.model';
import { appContext } from '../../models/getHelp/appContext';
import { ExperienceMetaData } from '../../models/getHelp/experienceConfigurations';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { getString } from '../../utils/stringLoader';

export interface IAnnouncementFloatProps extends IBaseProps, React.HTMLAttributes<HTMLElement> {}
// Todo: Brook  - maybe this component isn't needed since we no longer need showOsUpdateAtLaunch check - revist for clean up
export const AnnouncementFloat = (_props: IAnnouncementFloatProps): JSX.Element => {
  const [isVisible, setVisibility] = useState<boolean>(true);
  const configurations: ExperienceMetaData = useSelector<IAppState, ExperienceMetaData>(getHelpConfigSelector);
  const isGetHelp: boolean = useSelector<IAppState, boolean>(isGetHelpSelector);
  const notificationPayload: NotificationModel[] = useSelector<IAppState, NotificationModel[]>(notificationsSelector);
  const theme: IContactUsCurrentTheme = useSelector<IAppState, IContactUsCurrentTheme>(getThemeSelector);
  const ecsFlightingList: string[] = useSelector<IAppState, string[]>(
    (appState: IAppState) => appState.GlobalState?.getHelpECS?.flightList
  );
  const dispatch = useDispatch();
  const dispatchOnRender = useCallback(
    (notification: NotificationModel) => dispatch(dispatchOnNotificationsRender(notification)),
    [dispatch]
  );
  const dispatchOnDismiss = useCallback(
    (notification: NotificationModel) => {
      setVisibility(false);
      getHelpLogger.logAnnouncementDismissed();
      dispatch(dispatchDismissUserNotice(notification));
    },
    [dispatch]
  );

  if (!isVisible || (isGetHelp && appContext.appSupportsConfigUpdate && !configurations)) {
    return null;
  }

  const onLinkClick = async (link: string, notification: NotificationModel): Promise<void> => {
    if (isGetHelp) {
      const mergedList = (appContext.featureFlightList ?? []).concat(ecsFlightingList ?? []);
      getHelpLogger.logAnnouncementLinkClicked(link, notification.bucketId, notification.ruleId, mergedList);
    }
  };

  let element: JSX.Element = null;

  if (notificationPayload && notificationPayload.length > 0) {
    element = (
      <Notifications
        theme={theme}
        notifications={notificationPayload}
        onLinkClick={onLinkClick}
        onDismiss={dispatchOnDismiss}
        getString={getString}
        sanitize={!isGetHelp}
        onRender={dispatchOnRender}
      />
    );
  }

  return element;
};
